import { useEffect, useState } from 'react';
import { fetchWithAuth } from '../utils/auth';
import operatingMath from '../shared/cardcamOperatingCosts.js';

const PRICE_BASES = {
  average30: 'Recent purchase average',
  latest: 'Latest purchase',
  average: 'All purchase average',
  confirmed_acquisition: 'Recorded acquisition',
  recorded_acquisition: 'Recorded acquisition',
  raw_reference: 'Raw price reference',
  raw_market: 'Raw market reference',
  raw_retail: 'Raw retail reference',
};
const SOURCE_PREVIEW_LIMIT = 5;
const GBP = new Intl.NumberFormat('en-GB', {
  style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2,
});
const JPY = new Intl.NumberFormat('en-GB', {
  style: 'currency', currency: 'JPY', minimumFractionDigits: 0, maximumFractionDigits: 2,
});

function amount(value, currency = GBP) {
  if (value == null || value === '') return '—';
  const number = Number(value);
  return Number.isFinite(number) ? currency.format(number) : '—';
}

function EstimateMetric({ label, value, detail }) {
  return (
    <div className="cardcam-metric">
      <span className="rc-label">{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </div>
  );
}

function LoadingEstimate() {
  return (
    <div className="cardcam-metrics" role="status" aria-label="Loading cost estimate">
      {[0, 1, 2].map((key) => (
        <div key={key} className="cardcam-metric" aria-hidden="true">
          <span className="rc-skeleton rc-skeleton--line" />
          <span className="rc-skeleton rc-skeleton--title" />
          <span className="rc-skeleton rc-skeleton--line" />
        </div>
      ))}
    </div>
  );
}

export default function CardCamCostEstimate({ capture, canSeeFinancials }) {
  const [result, setResult] = useState({ key: '', data: null, error: '' });
  const captureID = capture?.id || '';
  const certificateNumber = String(capture?.identity?.certificationNumber || '').trim();
  const revision = capture?.revision || 0;
  const hammer = capture?.financials?.hammerGBP;
  const eligible = Boolean(
    captureID && capture?.identity?.kind === 'slab' &&
    String(capture.identity.gradingCompany || '').trim().toUpperCase() === 'ACE' &&
    /^\d{4,12}$/.test(certificateNumber)
  );
  const requestKey = JSON.stringify([captureID, certificateNumber, revision, hammer]);

  useEffect(() => {
    if (!canSeeFinancials || !eligible) return undefined;
    let active = true;
    const controller = new AbortController();
    const query = new URLSearchParams({ captureID });

    fetchWithAuth(`/api/cardcam/v2/captures/cost-estimate?${query}`, { signal: controller.signal })
      .then(async (response) => {
        const payload = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error(payload?.error?.message || 'The cost estimate is unavailable.');
        }
        const data = payload?.data;
        if (
          data?.version !== 'cardcam-average-cost-v1' || data.captureID !== captureID ||
          (data.certificateNumber != null && String(data.certificateNumber) !== certificateNumber) ||
          (data.status === 'estimated' && String(data.certificateNumber || '') !== certificateNumber)
        ) {
          throw new Error('The cost estimate did not match this capture.');
        }
        return data;
      })
      .then((data) => {
        if (active) setResult({ key: requestKey, data, error: '' });
      })
      .catch((error) => {
        if (active && error?.name !== 'AbortError') {
          setResult({ key: requestKey, data: null, error: error.message || 'The cost estimate is unavailable.' });
        }
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [canSeeFinancials, captureID, certificateNumber, eligible, requestKey]);

  if (!canSeeFinancials) return null;

  const current = result.key === requestKey ? result : null;
  const data = current?.data;
  const estimate = data?.estimate || {};
  const history = data?.priceHistory || {};
  const operating=operatingMath.forCost(data||{});
  const usedBasis = data?.policy?.usedBasis;
  const sourceLabel = data?.sourceLabel || PRICE_BASES[usedBasis] || 'Best available evidence';
  const warnings = Array.isArray(data?.warnings)
    ? data.warnings.filter((warning) => typeof warning === 'string' && warning.trim())
    : [];
  const sources = Array.isArray(data?.sources) ? data.sources : [];
  const sourceCount = Number.isSafeInteger(data?.sourceCount) && data.sourceCount >= sources.length
    ? data.sourceCount : sources.length;
  return (
    <section className="cardcam-section cardcam-cost-estimate" aria-label="Best available slab cost">
      <div className="cardcam-section-heading">
        <span>Best available cost</span>
        <small>{data ? sourceLabel : 'Checking price evidence'}</small>
      </div>
      <p className="rc-help">
        Automatically selected from available purchase and raw price evidence. Recorded acquisition costs remain unchanged.
      </p>

      {!eligible ? (
        <p className="rc-help">A verified ACE certificate is needed for this slab cost estimate.</p>
      ) : (
        <>
          {current?.error ? (
            <p className="rc-alert rc-alert--error" role="alert">{current.error}</p>
          ) : !current ? (
            <LoadingEstimate />
          ) : data ? (
            <>
              {data.status === 'not_found' ? (
                <p className="rc-alert rc-alert--warning">No usable purchase or raw price reference was found for this card.</p>
              ) : data.status === 'incomplete' ? (
                <p className="rc-alert rc-alert--warning">The price history is incomplete. Missing amounts remain unavailable.</p>
              ) : data.status !== 'estimated' ? (
                <p className="rc-alert rc-alert--warning">A cost estimate is unavailable for this capture.</p>
              ) : null}
              <div className="cardcam-metrics">
                <EstimateMetric label="Landed cost incl grading" value={amount(operating.landedCostInclGradingGBP)} detail={sourceLabel} />
                <EstimateMetric label="Actual hammer" value={amount(estimate.hammerGBP)} detail="Recorded auction price" />
                <EstimateMetric label="Est. profit after VAT and fees" value={amount(operating.profitGBP)} detail="Tom’s operating estimate. Before shipping and overheads" />
              </div>
              <p className="rc-help">
                {estimate.rawGBP != null ? `Card ${amount(estimate.rawGBP)}` : ''}
                {estimate.rawGBP != null && estimate.gradingGBP != null ? ` + import VAT ${amount(operating.importVATGBP)} + grading ${amount(estimate.gradingGBP)}. ` : ''}
                {operating.importTreatment==='unknown'?`Import treatment is unrecorded. Reference cost before import: ${amount(estimate.totalGBP)}. `:''}Cost estimates are separate from recorded acquisition cost and net profit.
              </p>

              <details className="rc-disclosure">
                <summary>Price evidence</summary>
                <div className="rc-disclosure__body">
                  <dl className="rc-kv" aria-label="Supplier price estimates">
                    <dt>Latest purchase</dt><dd>{amount(history.latestGBP)} · {amount(history.latestJPY, JPY)}</dd>
                    <dt>Recent purchase average</dt><dd>{amount(history.average30GBP)} · {amount(history.average30JPY, JPY)}</dd>
                    <dt>All purchase average</dt><dd>{amount(history.averageGBP)} · {amount(history.averageJPY, JPY)}</dd>
                  </dl>
                  {sources.length > SOURCE_PREVIEW_LIMIT ? (
                    <p className="rc-help">Showing {SOURCE_PREVIEW_LIMIT} of {sourceCount} source price records.</p>
                  ) : null}
                  {sources.length ? (
                    <div className="rc-table-wrap">
                      <table className="rc-table rc-table--stack">
                        <thead><tr><th>Order</th><th>Quantity</th><th>Unit JPY</th><th>Unit GBP</th></tr></thead>
                        <tbody>{sources.slice(0, SOURCE_PREVIEW_LIMIT).map((source, index) => (
                          <tr key={`${source.orderID || ''}:${source.lineID || ''}:${index}`}>
                            <td data-label="Order">{typeof source.rawLabel === 'string' && source.rawLabel.length > 0 && source.rawLabel.length <= 120 ? (
                              <a className="rc-link" href={`/supplier-orders?${new URLSearchParams({ q: source.rawLabel, ...(source.supplier ? { supplier: source.supplier } : {}) })}`}>
                                {source.orderID || 'Card Costs'}
                              </a>
                            ) : source.orderID || source.sourceRecordID || 'Price reference'}</td>
                            <td data-label="Quantity">{source.quantity ?? '—'}</td>
                            <td data-label="Unit JPY">{amount(source.unitJPY, JPY)}</td>
                            <td data-label="Unit GBP">{amount(source.unitGBP)}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  ) : <p className="rc-help">No source price records are available.</p>}
                  {warnings.length ? (
                    <ul className="rc-help">{warnings.map((warning, index) => <li key={`${index}:${warning}`}>{warning}</li>)}</ul>
                  ) : null}
                </div>
              </details>
            </>
          ) : null}
        </>
      )}
    </section>
  );
}
